import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getPasswordConfig } from '@/services/user.service';
import { createPasswordValidator, getPasswordRequirements } from '@/utils/passwordValidator';
import type { PasswordConfig } from '@/types/passwordConfig';

export default function usePasswordConfig(username?: string | null) {
  const [t, i18n] = useTranslation(['common']);

  const { data: config, isFetching } = useQuery({
    queryKey: ['users/password-configs', username],
    queryFn: async () => getPasswordConfig(username!),
    enabled: !!username,
    retry: false,
  });

  const passwordConfig = (config ?? null) as PasswordConfig | null;

  const validatePassword = useMemo(
    () => createPasswordValidator(passwordConfig, t),
    [passwordConfig, t, i18n.language]
  );

  const requirements = useMemo(
    () => getPasswordRequirements(passwordConfig, t),
    [passwordConfig, t, i18n.language]
  );

  return {
    config: passwordConfig,
    requirements,
    validatePassword,
    isFetching,
  };
}
